<template>
    <layout>
        <div v-if="isCiaEnabled" data-cy="browse-cover-title" class="text-base font-semibold p-4 pb-2">
            {{ $t('cia.search_form.title') }}
        </div>
        <div class="p-4">
            <template v-if="defaultState == 'cia'">
                <div class="flex w-full justify-between items-center">
                    <div class="flex w-3/5 lg:w-2/3">
                        <form-cia-basic-search
                            :processing="processing"
                            :search_form="search_form"
                            @formSubmit="handleFind"
                        />
                        <a-button
                            class="ml-2 mt-2"
                            v-if="covers_search_criteria && isCiaEnabled"
                            @click="handleReload"
                            data-cy="clear-cover-document-btn"
                            >{{ $t('cia.search_form.buttons.clear') }}</a-button
                        >
                    </div>
                    <div>
                        <a-button v-if="isCiaEnabled" @click="onUpload" data-cy="upload-cover-document-btn">
                            {{ $t('cia.search_form.buttons.upload') }}
                        </a-button>
                        <a-button
                            @click="$router.push({ name: 'create-cover' })"
                            type="primary"
                            data-cy="add-cover-document-btn"
                            :disabled="isAnAdmin"
                        >
                            <div class="block md:hidden">{{ $t('cia.search_form.buttons.add_cover') }}</div>
                            <div class="hidden md:block">{{ $t('cia.search_form.buttons.add_cover_document') }}</div>
                        </a-button>
                    </div>
                </div>
            </template>
            <template v-if="defaultState == 'basic'">
                <div class="flex w-full">
                    <div class="flex w-full lg:w-2/3">
                        <form-basic-search
                            :processing="processing"
                            :search_form="search_form"
                            @formSubmit="handleFind"
                        />
                    </div>
                </div>
                <div class="pt-2 font-semibold" v-if="covers_search_criteria" data-cy="previous-search-criteria">
                    {{ $t('covers.search_form.search_criteria') }}
                    <template v-for="(value, name, index) in covers_search_criteria" :key="index">
                        <span>
                            {{ name }}:
                            <span class="text-default-blue-green">{{ value }}</span>
                        </span>
                    </template>
                </div>
                <div class="border-t w-full absolute left-0 mt-2"></div>
            </template>
            <template v-if="defaultState == 'advance'">
                <div class="rounded-md shadow-md">
                    <form-advance-search
                        :processing="processing"
                        :search_form="search_form"
                        :form_mode="form_mode"
                        @formSubmit="handleFind"
                        @clear="handleClear"
                        @cancel="handleCancel"
                    />
                    <template v-if="covers_search_criteria">
                        <div class="flex">
                            <div
                                class="px-6 pt-2 pb-4 font-semibold flex w-3/4 md:w-5/6 lg:w-10/12 xl:w-11/12"
                                data-cy="previous-search-criteria"
                            >
                                {{ $t('covers.search_form.search_criteria') }}
                                <template v-for="(value, name, index) in covers_search_criteria" :key="index">
                                    <span>
                                        <span class="pl-1">{{ name }}:</span>
                                        <span class="text-default-blue-green">{{ value }}</span>
                                    </span>
                                </template>
                            </div>
                            <div class="flex w-1/4 md:w-1/6 lg:w-2/12 xl:w-1/12">
                                <div class="border-l h-full"></div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <div class="list-table overflow-x-auto overflow-visible" :class="{ 'pt-2 md:pt-4': !isCiaEnabled }">
                <div class="py-4" v-if="!isCiaEnabled">
                    <span class="font-semibold block pb-1 md:inline-block md:pb-0 md:pr-4" data-cy="search-items-found">
                        {{
                            $t('covers.search_form.search_results', {
                                total: covers_search_count,
                            })
                        }}
                    </span>
                    <a
                        data-cy="button-switch"
                        @click="handleSwitch"
                        class="underline text-default-blue-green hover:text-highlight-blue-green"
                    >
                        {{ switchButtonText }}
                    </a>
                </div>
                <covers-table
                    :search_id="search_id"
                    @onTableAction="onTableAction"
                    :search_criteria="covers_search_criteria"
                    @fetchSearchCriteria="handleFetchData"
                    @fetchSearchCount="handleFetchCount"
                    @clear="handleClear"
                    :section_type="isCiaEnabled ? 'cia' : 'cover'"
                    ref="CoversTable"
                />
            </div>
        </div>
        <modal-import-data @formSubmit="onImport" :processing="processing" ref="ImportDataModal" />
        <modal-cascade @formSubmit="handleCascadeSubmit" :cover="cascade_cover" ref="CascadeModal" />
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import CoversTable from '@/components/tables/Covers.vue';
import FormBasicSearch from '@/components/forms/covers/BasicSearch.vue';
import FormCiaBasicSearch from '@/components/forms/covers/CiaBasicSearch.vue';
import FormAdvanceSearch from '@/components/forms/covers/AdvanceSearch.vue';
import ModalImportData from '@/components/modals/CoverImportData.vue';
import ModalCascade from '@/components/modals/Cascade.vue';

import store from '@/store';

export default {
    components: {
        Layout,
        CoversTable,
        FormBasicSearch,
        FormCiaBasicSearch,
        FormAdvanceSearch,
        ModalImportData,
        ModalCascade,
    },
    data() {
        return {
            search_id: '',
            search_form: {
                basic_search: '',
                cover_id: '',
                cover_type: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                all_match: 1,
            },
            cascade_cover: {
                insurer_id: '',
                company_id: '',
                policy_number: '',
                product_code: '',
                insurer_policy_number: '',
                broker_policy_number: '',
                policy_start_date: null,
                policy_end_date: null,
                policy_date_range: {
                    start_date: null,
                    end_date: null,
                },
                cover_effective_date: null,
                cover_expiry_date: null,
                cover_date_range: {
                    start_date: null,
                    end_date: null,
                },
                certificate_code: '',
                cover_type: '',
                cover_status: 'active',
                manual_certificate_number: '',
                policyholder_address: '',
                risk_mortgagee: '',
                cover_category: '',
                section_1_label: '',
                section_1: '',
                section_2_label: '',
                section_2: '',
                section_3_label: '',
                section_3: '',
                section_4_label: '',
                section_4: '',
                registration_number: '',
                chassis_number: '',
                vehicle_make: '',
                vehicle_model: '',
                vehicle_year: null,
                vehicle_color: '',
                engine_number: '',
                sum_insured: 0,
                hpcc: '',
                seating: '',
            },
            cascade_cover_id: '',
            form_mode: 'results',
            search_values: {},
            covers_search_count: 0,
            covers_search_criteria: {},
            processing: false,
        };
    },
    mounted() {
        this.search_id = this.$route.params.id;
        this.$refs.CoversTable.fetch({}, this.search_id);
    },
    computed: {
        defaultState() {
            if (this.isCiaEnabled) {
                return 'cia';
            } else {
                return this.$store.state.system.search_state_covers;
            }
        },
        switchButtonText() {
            switch (this.$store.state.system.search_state_covers) {
                case 'basic':
                    return this.$t('covers.search_form.buttons.switch_basic');
                case 'advance':
                    return this.$t('covers.search_form.buttons.switch_advance');
            }
        },
        isCiaEnabled() {
            return this.$store.state.user.modules.cia;
        },
        isAnAdmin() {
            return (
                this.$store.state.user.abilities.includes('manage-association') ||
                this.$store.state.user.abilities.includes('manage-admin')
            );
        },
    },
    methods: {
        handleFind() {
            this.search_values = this.search_form;
            let years_from = 0;

            if (this.search_values.years_from !== 'all') {
                years_from = parseInt(this.search_values.years_from) || 0;
            }

            switch (this.defaultState) {
                case 'basic':
                    if (this.search_values.basic_search == '') {
                        this.$notification.error({
                            message: this.$t('covers.notifications.error.basic_empty'),
                        });
                        return;
                    }
                    break;
                case 'cia':
                    if (this.search_values.basic_search == '') {
                        this.$notification.error({
                            message: this.$t('covers.notifications.error.basic_empty'),
                        });
                        return;
                    }
                    this.search_values.reg_no = this.search_values.basic_search;
                    this.search_values.chassis_no = this.search_values.basic_search;
                    this.search_values.insurer_policy_number = this.search_values.basic_search;
                    this.search_values.broker_policy_number = this.search_values.basic_search;

                    this.search_values = {
                        ...this.search_values,
                        basic_search: '',
                        all_match: false,
                    };

                    years_from = 'all';

                    break;
                case 'advance':
                    if (
                        this.search_values.cover_id == '' &&
                        this.search_values.cover_type == '' &&
                        this.search_values.reg_no == '' &&
                        this.search_values.chassis_no == '' &&
                        this.search_values.engine_no == ''
                    ) {
                        this.$notification.error({
                            message: this.$t('covers.notifications.error.advance_empty'),
                        });
                        return;
                    }

                    if (
                        (this.search_values.cover_id != '' && this.search_values.cover_type == '') ||
                        (this.search_values.cover_id == '' && this.search_values.cover_type != '')
                    ) {
                        this.$notification.error({
                            message: this.$t('covers.notifications.error.coverid_or_covertype_required'),
                        });
                        return;
                    }

                    break;
            }

            this.processSearchValues({
                ...this.search_values,
                years_from: years_from,
            });
        },
        handleClear() {
            this.processing = true;

            this.search_form = {
                basic_search: '',
                cover_id: '',
                cover_type: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                all_match: 1,
            };

            this.processing = false;
        },
        handleCancel() {
            store.dispatch('system/updateSearchStateCovers', 'basic');

            this.handleClear();
        },
        handleFetchData(values) {
            this.covers_search_criteria = values.search_criteria;
            this.processing = false;
        },
        handleFetchCount(values) {
            this.covers_search_count = values.search_count;
        },
        handleReload() {
            this.$refs.CoversTable.clear();
            this.$router.push({ name: 'home-cover-search-all' });
        },
        processSearchValues(data) {
            this.processing = true;

            if (this.$store.getters['user/isMultiAssociation']) {
                data.association_id = this.$store.getters['user/currentAssociationId'];
            }

            this.$http
                .post('/api/covers/search', data)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$router.push({
                            name: 'home-cover-search',
                            params: {
                                id: response.data.id,
                            },
                        });

                        this.search_id = response.data.id;
                        this.$refs.CoversTable.selected_row_keys = [];
                        this.$refs.CoversTable.fetch({}, this.search_id);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVSB01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handleSwitch() {
            if (this.$store.state.system.search_state_covers == 'basic') {
                store.dispatch('system/updateSearchStateCovers', 'advance');
            } else {
                store.dispatch('system/updateSearchStateCovers', 'basic');
            }

            this.handleClear();
        },
        onTableAction(data) {
            switch (data.key) {
                case 'edit':
                    this.$router.push({
                        name: 'edit-cover',
                        params: {
                            id: data.id,
                        },
                    });
                    break;
                case 'cascade':
                    this.onCascadeCover(data.id);
                    break;
                case 'delete':
                    break;
                case 'print':
                    break;
            }
        },
        onCascadeCover(id) {
            this.cascade_cover_id = id;

            this.$http
                .get('/api/covers/' + id)
                .then((response) => {
                    this.cascade_cover = {
                        ...this.cascade_cover,
                        ...response.data.data,
                        id: null,
                        insurer_id: response.data.data.company_id,
                        insurer_name: response.data.data.name,
                        company_id: response.data.data.intermediary_id,
                        policy_date_range: {
                            start_date: this.$dayjs(response.data.data.policy_start_date),
                            end_date: this.$dayjs(response.data.data.policy_end_date),
                        },
                        cover_date_range: {
                            start_date: this.$dayjs(response.data.data.cover_effective_date),
                            end_date: this.$dayjs(response.data.data.cover_expiry_date),
                        },
                        printed_first_at:
                            response.data.data.printed_first_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.printed_first_at).format('YYYY-MM-DD HH:mm:ss'),
                        printed_last_at:
                            response.data.data.printed_last_at == null
                                ? 'N/A'
                                : this.$dayjs(response.data.data.printed_last_at).format('YYYY-MM-DD HH:mm:ss'),
                        printed_last_by:
                            response.data.data.printed_last_by == null ? 'N/A' : response.data.data.printed_last_by,
                    };
                    this.$refs.CascadeModal.showModal();
                })
                .catch((error) => {
                    this.$notification.error({
                        message: response.data.message,
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handleCascadeSubmit(data) {
            let submit_data = {
                keys: [],
            };

            Object.keys(data).forEach((key, index) => {
                if (data[key]) {
                    submit_data.keys.push(key);
                }
            });

            this.processing = true;

            this.$http
                .post('/api/covers/cascade/' + this.cascade_cover_id, submit_data)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });
                        this.$refs.CoversTable.reload();
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCS04' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onUpload() {
            this.$refs.ImportDataModal.showModal();
        },
        onImport(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/covers/import', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.ImportDataModal.fileList = [];
                        this.$refs.ImportDataModal.modal_visible = false;
                        this.$refs.CoversTable.reload();
                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVS002' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>
